import api from "lib/api";
import { useCallback } from "react";
import produce from "immer";
import { State } from "hooks/usePagedData";
import { createErrorAlert } from "lib/api/errors";
import { useDispatch } from "react-redux";
import { createAlertAction } from "data/app/appActions";
import { AxiosError } from "axios";
import { Workflow } from "components/Workflows/Workflows";
import { createAlert } from "records/Alert";
import { useTranslation } from "react-i18next";

export function useWorkflowList(
	state: State,
	patchState: (data: State) => void
) {
	const reduxDispatch = useDispatch();
	const { t } = useTranslation();

	/**
	 * Updating state to the correct offset
	 *
	 * @param id workflow id
	 */
	const produceOffsetPatch = useCallback(
		(id: string) =>
			produce(state, draft => {
				const index = draft.results.findIndex(
					(workflow: Workflow) => workflow.id === id
				);
				draft.results.splice(index, 1);
				const newOffset = draft.offset - 1;
				draft.offset = newOffset;
			}),
		[state]
	);

	/**
	 * Deleting a workflow off the workflows page
	 *
	 * @param id workflow id
	 */
	const deleteWorkflow = useCallback(
		async (id: string) => {
			try {
				await api.workflow.deleteWorkflowDefinition(id, {
					handleErrors: true,
				});
				patchState(produceOffsetPatch(id));
				reduxDispatch(
					createAlertAction(
						createAlert({
							title: t("alerts.workflowDeleted.title"),
							status: "success",
							timeout: 3000,
						})
					)
				);
			} catch (error) {
				const e = error as AxiosError;
				reduxDispatch(
					createAlertAction(
						createErrorAlert(e, "deleteWorkflowError", {
							id,
						})
					)
				);
			}
		},
		[patchState, produceOffsetPatch, reduxDispatch, t]
	);

	return {
		deleteWorkflow,
	};
}
